import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useApi } from "@/hooks/useApi";
import type { QueryStatus } from "react-query";
import { getActiveSpan, getRootSpan, setMeasurement, startInactiveSpan, type Span } from "@sentry/nextjs";
const SECTION_OP = "ui.section.load";

// @sentry/nextjs re-exports neither SPAN_STATUS_ERROR nor the status codes.
const SPAN_STATUS_ERROR = 2;
type OpenSection = {
  span: Span;
  rootSpan: Span;
  startedAt: number;
};

/**
 * Times how long one section of a page waits for its data, e.g.
 * `useSectionTiming("logs.results", logsQuery.status)`.
 *
 * Pass the react-query status of every fetch the section renders from, in
 * dependency order for a chain: `useSectionTiming("job.metrics",
 * [detailsStatus, metricsStatus])` measures the wait the user actually sees and
 * settles on the first failure instead of hanging. `"idle"` counts as waiting,
 * which is what a query still gated behind an unresolved dependency reports.
 *
 * Emits a `section.<id>` child span on the in-flight pageload/navigation
 * transaction (so a Sentry trace reads as a waterfall of sections, each with
 * its own `http.client` children) plus a same-named measurement, which makes
 * `measurements["section.logs.results"]` queryable per route in Discover,
 * dashboards, and alerts. A failed section reports no measurement, so error
 * responses cannot flatter the percentiles.
 *
 * A section is timed once per transaction, so a refetch on a settled page is
 * not counted (it has no transaction to attach to), while a section that stays
 * mounted across a soft navigation is timed again on the new transaction.
 *
 * Every successful wait is also reported fire-and-forget to
 * `/frontend/metrics` so section waits chart over time in Grafana. This
 * reporting is independent of Sentry sampling: it fires on every page view,
 * not just the fraction with a sampled transaction.
 */
export function useSectionTiming(id, status) {
  const $ = _c(19);
  let t0;
  if ($[0] !== status) {
    t0 = Array.isArray(status) ? status : [status];
    $[0] = status;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const statuses = t0;
  let t1;
  if ($[2] !== statuses) {
    t1 = statuses.every(_temp);
    $[2] = statuses;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const ready = t1;
  let t2;
  if ($[4] !== statuses) {
    t2 = statuses.includes("error");
    $[4] = statuses;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const failed = t2;
  const api = useApi();
  const open = useRef(null);
  const settledRootSpanId = useRef(null);
  const waitStartedAt = useRef(null);
  let t3;
  let t4;
  if ($[6] !== api || $[7] !== failed || $[8] !== id || $[9] !== ready) {
    t3 = () => {
      if (waitStartedAt.current === null) {
        if (!ready && !failed) {
          waitStartedAt.current = performance.now();
        }
        return;
      }
      if (!ready && !failed) {
        return;
      }
      if (ready) {
        api.post("/frontend/metrics", {
          section_id: id,
          section_duration_ms: performance.now() - waitStartedAt.current
        }).catch(_temp2);
      }
      waitStartedAt.current = null;
    };
    t4 = [api, id, ready, failed];
    $[6] = api;
    $[7] = failed;
    $[8] = id;
    $[9] = ready;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[12] !== failed || $[13] !== id || $[14] !== ready) {
    t5 = () => {
      if (!open.current) {
        const activeSpan = getActiveSpan();
        const rootSpan = activeSpan ? getRootSpan(activeSpan) : undefined;
        if (!rootSpan) {
          return;
        }
        const rootSpanId = rootSpan.spanContext().spanId;
        if (rootSpanId === settledRootSpanId.current) {
          return;
        }
        if (ready || failed) {
          settledRootSpanId.current = rootSpanId;
          return;
        }
        open.current = {
          span: startInactiveSpan({
            name: `section.${id}`,
            op: SECTION_OP,
            parentSpan: rootSpan,
            attributes: {
              "section.id": id
            }
          }),
          rootSpan,
          startedAt: performance.now()
        };
        return;
      }
      if (!ready && !failed) {
        return;
      }
      const {
        span,
        rootSpan: rootSpan_0,
        startedAt
      } = open.current;
      if (failed) {
        span.setStatus({
          code: SPAN_STATUS_ERROR,
          message: "internal_error"
        });
      } else {
        if (rootSpan_0.isRecording()) {
          setMeasurement(`section.${id}`, performance.now() - startedAt, "millisecond", rootSpan_0);
        }
      }
      span.end();
      open.current = null;
      settledRootSpanId.current = rootSpan_0.spanContext().spanId;
    };
    t6 = [id, ready, failed];
    $[12] = failed;
    $[13] = id;
    $[14] = ready;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      const section = open.current;
      if (!section) {
        return;
      }
      section.span.setStatus({
        code: SPAN_STATUS_ERROR,
        message: "cancelled"
      });
      section.span.end();
      open.current = null;
    };
    t8 = [];
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
}
function _temp2() {}
function _temp(one) {
  return one === "success";
}